// src/components/gestionEvento/MobileCategoriasView.jsx
import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Stack, IconButton, Divider, List,
  ListItemButton, ListItemSecondaryAction, alpha, InputBase,
  Checkbox, Button
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// --- util fecha nac ---
const calcularRangoFechasNacimiento = (fechaCorte, edadMin, edadMax) => {
  const corte = fechaCorte ? new Date(`${fechaCorte}T00:00:00`) : null;
  if (!corte || isNaN(corte)) return '—';
  const y = corte.getFullYear();
  const minDate = new Date(y - Number(edadMax || 0), 0, 1);
  const maxDate = new Date(y - Number(edadMin || 0), 11, 31);
  const fmt = (d) => d.toLocaleDateString('es-ES', { day:'2-digit', month:'2-digit', year:'numeric' });
  return `${fmt(minDate)} – ${fmt(maxDate)}`;
};

// --- item sortable ---
function SortableCategoria({ c, idx, fechaCorteDefault, onOpenDetalle, checked, onToggleCheck }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: c.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 'auto',
  };

  const rangoEdad = `${c?.edadMin ?? '—'}–${c?.edadMax ?? '—'} años`;
  const rangoNac = calcularRangoFechasNacimiento(
    c?.fechaCorte || fechaCorteDefault,
    c?.edadMin,
    c?.edadMax
  );

  return (
    <Box ref={setNodeRef} style={style} sx={{ mb: 1.25, width: '100%' }}>
      <ListItemButton
        onClick={() => onOpenDetalle?.(c.id)}
        sx={(t) => ({
          width: '100%',
          alignItems: 'flex-start',
          borderRadius: 3,
          bgcolor: alpha(t.palette.common.black, 0.015),
          border: `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
          '&:hover': { bgcolor: alpha(t.palette.primary.main, 0.04) },
          ...(checked
            ? {
                bgcolor: alpha(t.palette.error.main, 0.08),
                border: `2px solid ${alpha(t.palette.error.main, 0.6)}`,
              }
            : {}),
        })}
      >
        {/* checkbox */}
        <Checkbox
          edge="start"
          checked={!!checked}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleCheck?.(c.id)}
          sx={{ mr: 1, mt: 0.25 }}
        />

        <Stack spacing={0.5} sx={{ width: '100%', pr: 6 }}>
          {/* 1) Nombre + #cat */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700 }}>
              {c.nombre}
            </Typography>
            <Typography
              sx={(t)=>({
                fontWeight: 600,
                fontSize: '0.85rem',
                color: t.palette.primary.main,
              })}
            >
              #{c.catNumber ?? '—'}
            </Typography>
          </Stack>

          {/* 2) Rango de edad */}
          <Typography sx={{ color:'text.secondary', fontSize: '0.95rem' }}>
            {rangoEdad}
          </Typography>

          {/* 3) Rango de nacimientos */}
          <Typography sx={{ color:'text.disabled', fontSize: '0.8rem' }}>
            {rangoNac}
          </Typography>
        </Stack>

        <ListItemSecondaryAction sx={{ display:'flex', alignItems:'center', gap: 0.5 }}>
          {/* Handle drag */}
          <IconButton
            edge="end"
            aria-label="reordenar"
            {...listeners}
            {...attributes}
            sx={{
              touchAction: 'none',
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
            }}
            onClick={(e) => e.stopPropagation()} // evita abrir detalle al tocar el handle
          >
            <DragIndicatorIcon />
          </IconButton>

          <IconButton edge="end" aria-label="abrir" onClick={() => onOpenDetalle?.(c.id)}>
            <ChevronRightIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItemButton>
      <Divider sx={{ opacity: 0.2 }} />
    </Box>
  );
}

export default function MobileCategoriasView({
  categorias = [],
  search = '',
  setSearch = () => {},
  onOpenAddPersonalizada,
  onOpenAddPredefinidas,
  onOpenDetalle,
  fechaCorteDefault,
  // para reordenar igual que en la tabla:
  competenciaId,
  onReorderCategorias,
  // nuevo: para eliminar múltiples
  onRequestDeleteMultiple,  // (ids: string[]) => Promise<void>
  onDeleteCategoria,        // opcional fallback por si no pasas onRequestDeleteMultiple
}) {
  // filtro
  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase();
    if (!q) return categorias;
    return categorias.filter(c =>
      c?.nombre?.toLowerCase().includes(q) ||
      c?.codigo?.toLowerCase?.().includes(q)
    );
  }, [categorias, search]);

  // selección múltiple
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleCheck = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // sensores para móvil
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // bloqueo del scroll mientras se arrastra
  const lockScroll = () => { document.body.style.overflow = 'hidden'; };
  const unlockScroll = () => { document.body.style.overflow = ''; };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    unlockScroll();
    if (!over || active.id === over.id) return;

    const oldIndex = filtered.findIndex((x) => x.id === active.id);
    const newIndex = filtered.findIndex((x) => x.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const fullOld = categorias.findIndex((x) => x.id === active.id);
    const fullNew = categorias.findIndex((x) => x.id === over.id);
    if (fullOld === -1 || fullNew === -1) return;

    const newList = arrayMove(categorias, fullOld, fullNew);
    const withNumbers = newList.map((cat, i) => ({ ...cat, catNumber: i + 1 }));
    onReorderCategorias?.(competenciaId, withNumbers);
  };

  const handleDragStart = () => lockScroll();
  const handleDragCancel = () => unlockScroll();

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (onRequestDeleteMultiple) {
      await onRequestDeleteMultiple(ids);
    } else if (onDeleteCategoria) {
      await Promise.all(ids.map((id) => onDeleteCategoria(id)));
    }
    clearSelection();
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, pb: 10 }}>
      {/* Buscador pill */}
      <Box
        sx={(t)=>({
          display:'flex', alignItems:'center',
          gap: 1, px: 1.25, py: 0.75, mb: 1.5,
          borderRadius: 999,
          bgcolor: alpha(t.palette.background.paper, 0.9),
          border: `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
          boxShadow: `0 4px 16px ${alpha(t.palette.common.black, 0.06)}`,
        })}
      >
        <SearchIcon sx={{ opacity: 0.6 }} />
        <InputBase
          value={search}
          onChange={(e)=>setSearch(e.target.value)}
          placeholder="Buscar por código o nombre…"
          inputProps={{ 'aria-label':'buscar' }}
          sx={{ flex: 1, fontSize:'0.95rem' }}
        />
      </Box>

      {/* Encabezado + acciones si hay selección */}
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
          Categorías existentes
        </Typography>

        {selectedIds.size > 0 && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              onClick={handleBulkDelete}
              color="error"
              variant="contained"
              startIcon={<DeleteIcon />}
              sx={{
                borderRadius: 2.5,
                textTransform: 'none',
                fontWeight: 800,
                px: 2,
              }}
            >
              Eliminar
            </Button>
          </Stack>
        )}
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={filtered.map((c) => c.id)}
          strategy={verticalListSortingStrategy}
        >
          <List disablePadding sx={{ width: '100%' }}>
            {filtered.map((c, idx) => (
              <SortableCategoria
                key={c.id}
                c={c}
                idx={idx}
                fechaCorteDefault={fechaCorteDefault}
                onOpenDetalle={onOpenDetalle}
                checked={selectedIds.has(c.id)}
                onToggleCheck={toggleCheck}
              />
            ))}
            {filtered.length === 0 && (
              <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                <Typography>No se encontraron categorías.</Typography>
              </Box>
            )}
          </List>
        </SortableContext>
      </DndContext>
    </Box>
  );
}
